import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';

import ActivePlansCard from './sections/ActivePlansCard/ActivePlansCard';
import ClientDataCard from './sections/ClientDataCard/ClientDataCard';
import ClientQuotesCard from './sections/ClientQuotesCard/ClientQuotesCard';
import PlansInSetupCard from './sections/PlansInSetupCard/PlansInSetupCard';
import { ReactComponent as ClientIcon } from '../../../../assets/icons/icon-client.svg';
import { ReactComponent as ErrorIcon } from '../../../../assets/icons/xerror.svg';
import BannerFill from '../../../../shared/components/Globals/BannerFill/BannerFill';
import EmptyState from '../../../../shared/components/Globals/EmptyState/EmptyState';
import SecondaryHeader from '../../../../shared/components/Globals/Headers/SecondaryHeader/SecondaryHeader';
import { buildClientProfileRoute, CLIENT_PROFILE_STATUS_PARAM, CLIENTS_ROUTE } from '../../../../shared/constants/auth';
import { CLIENT_STATUS_BADGE } from '../../../../shared/constants/clients';
import { useClientQuotes } from '../../../../shared/hooks/useClientQuotes/useClientQuotes';
import type { IClientProfileLocationState } from '../../../../shared/interfaces/clients';
import { TRANSLATIONS_CLIENTS } from '../../../../shared/translations/clients';
import { ClientsStatus } from '../../../../shared/types/clients';
import { resolveClientProfileStatus } from '../../../../shared/utils/clients/clients';
import QuoteSuccessModal from '../../../Prospects/sections/QuoteEditSuccessModal/QuoteSuccessModal';

import './ClientProfile.scss';

interface IQuoteEditedState {
  quoteId: number | null;
  quoteName: string;
  companyName: string;
  prospectId: number;
  action?: 'create' | 'update';
}

type ClientProfileLocationState = IClientProfileLocationState & { quoteEdited?: IQuoteEditedState };

const ClientProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { state } = useLocation() as { state: ClientProfileLocationState | null };
  const [quoteEditedModal, setQuoteEditedModal] = useState<IQuoteEditedState | null>(null);

  const clientId = Number(id);
  const hasValidId = id !== undefined && !Number.isNaN(clientId);

  const status = resolveClientProfileStatus(searchParams.get(CLIENT_PROFILE_STATUS_PARAM), state?.status);
  const missingDocumentValue = status === ClientsStatus.Active && !state?.documentValue;
  const shouldRedirect = !status || missingDocumentValue;
  const { prospect, quotes, loading, refetch } = useClientQuotes(clientId, status, state?.documentValue);

  useEffect(() => {
    if (hasValidId && shouldRedirect) void navigate(CLIENTS_ROUTE, { replace: true });
  }, [hasValidId, shouldRedirect, navigate]);

  useEffect(() => {
    if (!state?.quoteEdited) return;

    setQuoteEditedModal(state.quoteEdited);
    void refetch();
    void navigate('.', {
      replace: true,
      state: { status: state.status, companyName: state.companyName, documentValue: state.documentValue }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.quoteEdited]);

  if (!hasValidId) {
    return (
      <div className="client-profile">
        <SecondaryHeader
          breadcrumbItems={[{ label: t(TRANSLATIONS_CLIENTS.profile.back), onClick: () => navigate(CLIENTS_ROUTE) }]}
        />
        <EmptyState
          icon={<ClientIcon width={28} height={28} aria-hidden="true" />}
          title={t(TRANSLATIONS_CLIENTS.profile.notFound)}
        />
      </div>
    );
  }

  if (shouldRedirect) return null;

  const hasActivationError = status === ClientsStatus.AwaitingStart && Boolean(prospect?.error);
  const statusBadge = CLIENT_STATUS_BADGE[status];
  const badgeText = hasActivationError
    ? t(TRANSLATIONS_CLIENTS.clientsList.badges.activationError)
    : t(statusBadge.labelKey);
  const badgeVariant = hasActivationError ? 'lost' : statusBadge.variant;
  const companyName = state?.companyName ?? prospect?.companyName ?? '';

  const breadcrumbItems = [
    {
      label: t(TRANSLATIONS_CLIENTS.profile.back),
      onClick: () => navigate(CLIENTS_ROUTE, { state: { status } })
    },
    { label: state?.companyName ?? '' }
  ];

  return (
    <div className="client-profile">
      <SecondaryHeader breadcrumbItems={breadcrumbItems} badgeText={badgeText} badgeVariant={badgeVariant} />

      {hasActivationError && (
        <div className="client-profile__alert">
          <BannerFill
            variant="danger"
            inline
            regularText
            icon={<ErrorIcon aria-hidden="true" />}
            title={t(TRANSLATIONS_CLIENTS.profile.activationError.alert, { company: companyName })}
          />
        </div>
      )}

      <div className="client-profile__body">
        <div className="client-profile__main">
          <ActivePlansCard
            clientId={clientId}
            status={status}
            quotes={quotes}
            quotesLoading={loading}
            prospect={prospect}
            onRefresh={refetch}
          />
          <PlansInSetupCard
            status={status}
            quotes={quotes}
            prospect={prospect}
            onActivated={refetch}
            onQuoteUpdate={refetch}
          />
          <ClientQuotesCard
            status={status}
            prospect={prospect}
            quotes={quotes}
            loading={loading}
            onQuoteStatusChange={refetch}
            returnTo={{
              path: buildClientProfileRoute(clientId, status),
              state: { status, companyName: state?.companyName, documentValue: state?.documentValue }
            }}
          />
        </div>
        <aside className="client-profile__sidebar">
          <ClientDataCard status={status} id={hasValidId ? clientId : null} />
        </aside>
      </div>

      {quoteEditedModal && (
        <QuoteSuccessModal
          open
          quoteName={quoteEditedModal.quoteName}
          companyName={quoteEditedModal.companyName}
          mode={quoteEditedModal.action ?? 'update'}
          quoteId={quoteEditedModal.quoteId}
          prospectId={quoteEditedModal.prospectId}
          onClose={() => setQuoteEditedModal(null)}
        />
      )}
    </div>
  );
};

export default ClientProfile;
